import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import React, {JSX, useEffect, useRef, useState} from 'react';
import IconButton from '@mui/material/IconButton';
import { Button, Chip, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { queryCache } from 'react-query';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from "@mui/icons-material/Check";
import { isNil } from "ramda";
import {
  addThousandsSeparator,
  Application,
  ApplicationFormConfig,
  ApplicationQuoteType, applicationQuoteTypeLabels,
  ApplicationWithMappedCustomers,
  CashDealApplication,
  CashDealCustomer,
  ClientApplicationType,
  ColumnInfo,
  createDefaultCustomer,
  ICustomField,
  LoadingPanel,
  PermissionSet,
  useDebounce
} from "@amsanalytics/react-shared-library";
import { GridCellProps, GridDataStateChangeEvent, GridFilterCellProps, GridRowClickEvent, GridRowProps, GridToolbar } from '@progress/kendo-react-grid';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { DateRangePicker, DateRangePickerChangeEvent, DateRangePickerDateInputSettings } from '@progress/kendo-react-dateinputs';
import { Button as KendoButton } from "@progress/kendo-react-buttons";
import moment from 'moment';
import produce from 'immer';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { hasValue } from "@amsanalytics/react-shared-library";
import localforage from 'localforage';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {ApplicationDispatch, updateApplicationQueueFilterSuccess} from "../../../../store/application/types";
import {IEmployeeRole} from "../../../../Interfaces/Admin/IEmployeeRole";
import {IAppStatus} from "../../../../Interfaces/Application/IAppStatus";
import {IApplicationQueue} from "../../../../Interfaces/Common/IApplicationQueue";
import {IApplicationQueueLoadingDetail} from "../../../../Interfaces/Common/IApplicationQueueLoadingDetail";
import {AppState} from "../../../../store/rootReducer";
import {IEmployee} from "../../../../Interfaces/Admin/IEmployee";
import {ILoggedInEmployee} from "../../../../Interfaces/Admin/ILoggedInEmployee";
import { useQueryApplicationList } from "../../../../Data/ApplicationData";
import { fetchFile } from "../../../../Data/fetch";
import {getEmployeeName, saveFile, userHasPermission} from "../../../../utils/utils";
import {
  createApp,
  createAppFromDealerForm,
  loadApps,
  moveForwardAppFromQueue,
  setEditingApp,
  showErrorMessage
} from "../../../../store/application/actions";
import { createCashDealApplication } from "../../../../store/application/types";
import styles from './_application-queue.module.scss';
import {fetchConfig, httpPostAsync} from "../../../../utils/HttpClient";
import { getApplicationDownloadMode } from "../../../../utils/applicationDownload";
import { authService } from "../../../..";
import {Constants, millisecondsToRefreshQueue, OrganizationIds, permissions} from "../../../../utils/constants";
import {
  allApplicationRowChecklistsAreComplete,
  canViewAnyAvailableChecklist, getAssignedEmployeeByRoleAndSequence,
  isAdmin,
  isInRole,
  isSalesPerson,
  isUnderwriter
} from "../../../../utils/appUtils";
import ErrorDialog from "../../../Dialogs/ErrorDialog";
import DealerApplicationFormDialog from "../../../Dialogs/DealerApplicationFormDialog/DealerApplicationFormDialog";
import {DealerApplicationSubmission} from "../../../../Interfaces/DealerApplication/DealerApplicationSubmission";
import ApplicationFormDialog from "../../../Dialogs/ApplicationFormDialog";
import {IApplicationListRow} from "../../../../Interfaces/Application/IApplicationListRow";
import {IApplicationRowChecklist} from "../../../../Interfaces/Application/IApplicationRowChecklist";
import {IApplicationStatusWorkflowItem} from "../../../../Interfaces/Application/IApplicationStatusWorkflowItem";
import {ChecklistRowButtonToolTipText} from "../../ApplicationChecklistViewer";
import {viewCommentsReceivedForApplication} from "../../../../Interfaces/Common/IDealerServiceAppHistory";
import ReportViewerDialog from "../../../Dialogs/ReportViewerDialog/ReportViewerDialog";
import {useDialog} from "../../../../providers/DialogProvider";
import {useAvailableStatusWorkflowActions} from "../../../../hooks/useAvailableStatusWorkflowActions";
import {useAllowedQueueNavigationStatuses} from "../../../../hooks/useAllowedQueueNavigationStatuses";

export interface Locale {
  language: string;
  locale: string;
}
const locales: Locale[] = [
  {
    language: 'en-US',
    locale: 'en',
  },
  {
    language: 'es-ES',
    locale: 'es',
  },
];

// custom cell for the "AppStatus" column. we will use the statusBackgroundColor and the statusTextColor to color the Chip component for the status (if they exist)
const StatusCell = (props: GridCellProps & {
  onClick: (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
  queueRowAction?: IApplicationStatusWorkflowItem;
  onQueueRowActionClick?: (e: React.MouseEvent<HTMLButtonElement>, item: IApplicationListRow, workflowItem: IApplicationStatusWorkflowItem) => void;
}) => {
  // center the chip in the middle of the cell
  return <td className={styles.statusCell} onClick={(e) => { e.stopPropagation(); props.onClick(e); }}>
    <Tooltip title={props.dataItem.status}>
      <Chip
        label={props.dataItem.status}
        variant="outlined"
        style={{
          cursor: 'pointer',
          backgroundColor: props.dataItem.statusBackgroundColor ?? 'inherit',
          color: props.dataItem.statusTextColor ?? 'inherit',
          borderColor: props.dataItem.statusBackgroundColor ?? 'inherit',
          width: 'fit-content',
          height: 'fit-content',
          fontSize: '14px',
          lineHeight: '20px',
          borderRadius: '10px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'inline-block',
          maxWidth: '100%',
        }}
      />
    </Tooltip>
    {props.queueRowAction && props.onQueueRowActionClick && (
      <Tooltip title={props.queueRowAction.label}>
        <IconButton
          size='small'
          color='success'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            props.onQueueRowActionClick!(e, props.dataItem as IApplicationListRow, props.queueRowAction!);
          }}
        >
          <ArrowForwardIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    )}
  </td>;
}

interface ApplicationQueueGridColumn extends ColumnInfo {
  filtercell?: React.ComponentType<GridFilterCellProps>;
  filterTitle?: string;
}

interface ApplicationQueueProps {
  applicationQueue: IApplicationQueue;
  applicationQueueLoadingDetail: IApplicationQueueLoadingDetail;
  defaultStatus: string;
  onSelect?: (appId: string) => void;
  configuration: IConfiguration;
  tabsOnLeft?: boolean;
}

export default function ApplicationQueue(props: ApplicationQueueProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<ApplicationDispatch>();
  const configuration = props.configuration;
  const employees = useSelector((state: AppState) => state.applications.employees);
  const employeeRoles: IEmployeeRole[] = useSelector((state: AppState) => state.applications.employeeRoles);
  const { getAvailableActions } = useAvailableStatusWorkflowActions();
  const {
    allowedStatuses: allowedQueueNavigationStatuses,
    isRestricted: isQueueNavigationRestrictedByRole,
  } = useAllowedQueueNavigationStatuses();
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const appStatuses: IAppStatus[] = useSelector((state: AppState) => state.applications.appStatuses);
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const loggedInUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const exporterRef = useRef<any>(null);
  const debouncedDataState = useDebounce(props.applicationQueueLoadingDetail.filters, 500); // Wait 500ms after user stops typing
  const [openDialog, closeDialog] = useDialog();
  const [currentLocale, setCurrentLocale] = useState<Locale>(locales[0]);
  const applicantCreditAppFormConfig: ApplicationFormConfig | undefined = useSelector((state: AppState) => state.applications.applicantCreditAppFormConfiguration);
  const coApplicantCreditAppFormConfig: ApplicationFormConfig | undefined = useSelector((state: AppState) => state.applications.coApplicantCreditAppFormConfiguration);
  // List of custom fields for this client.
  const customFields: ICustomField[] = useSelector((state: AppState) => state.applications.customFields.sort((a, b) => a.name.localeCompare(b.name)));
  // Get data based on user's sort/filter/pagination settings.
  const { resolvedData } = useQueryApplicationList(props.applicationQueue.id, props.applicationQueueLoadingDetail.count, { dataState: debouncedDataState });
  // Whether or not to show the Application Type selector window
  const [showApplicationTypeSelector, setShowApplicationTypeSelector] = useState<boolean>(false);
  // Cash Deal Application state
  const states = useSelector((state: AppState) => state.applications.states);
  const organizations = useSelector((state: AppState) => state.applications.lookups.organizations);
  const [showCashDealWindow, setShowCashDealWindow] = useState<boolean>(false);
  const [cashDealCustomer, setCashDealCustomer] = useState<Partial<CashDealCustomer>>({});
  const [cashDealLocationId, setCashDealLocationId] = useState<string | null>(null);
  const pagedOutput = resolvedData?.applications;
  // list of dismissed release note IDs
  const [dismissedReleaseNotesId, setDismissedReleaseNotesId] = useState<string[]>([]);
  // Whether we are showing release notes. This affects the height of the header, which affects the height of tabs/grids.
  const now = new Date();
  const hasCurrentReleaseNotes: boolean = useSelector((state: AppState) => state.applications.releaseNotes.some((releaseNote) => !dismissedReleaseNotesId.includes(releaseNote.id) && new Date(releaseNote.startDate) <= now && now < new Date(releaseNote.expirationDate)));
  // Get the release note ID to ignore from localforage
  const getReleaseNoteIdsToIgnore = async () => {
    const dismissedReleaseNotesIds = await localforage.getItem('dismissedReleaseNoteIds') as string[] || [];
    setDismissedReleaseNotesId(dismissedReleaseNotesIds);
  }

  const editOrder = (id: string) => {
    if (props.onSelect) {
      props.onSelect(id);
    } else {
      navigate(`/order/${id}`);
    }
  }

  // User has clicked the Download button. Mode is decided by getApplicationDownloadMode so the
  // UseApplicationReportOnly feature flag is honored consistently with ApplicationDetailsCard.
  const downloadApp = async (id: string, appNumber: number) => {
    const mode = getApplicationDownloadMode(configuration);
    if (mode === 'hidden') return;
    if (mode === 'report') {
      const bearerToken = await authService.getToken();
      const applicationReport = await httpPostAsync(`api/reporting/getReport`,
        {
          reportName: configuration.applicationReport,
          reportParameters: [{
            name: "AppNumber",
            value: appNumber,
          },
          ],
          reportFormat: "1"
        }, bearerToken!);

      await saveFile(applicationReport!, 'applicationReport.pdf');
    } else {
      const response = await fetchFile(`api/pdf/${id}`, dispatch);
      if (response.ok) {
        await saveFile(response, 'application.pdf');
      } else {
        dispatch(showErrorMessage("Error retrieving PDF"));
      }
    }
  }

  useEffect(() => {
    dispatch(setEditingApp(undefined));
    // get dismissed release note ids
    getReleaseNoteIdsToIgnore();
  }, []);
  useEffect(() => {
    dispatch(loadApps());
  }, [dispatch]);

  useEffect(() => {
    let timer = setInterval(async () => {
      await dispatch(loadApps());
      await queryCache.refetchQueries(`application/${props.applicationQueue.id}/`);
    }, millisecondsToRefreshQueue);
    return () => clearInterval(timer);
  }, []);

  // Returns true if the user can create a new application, otherwise false.
  const canEmployeeCreateNewApplication =
    isAdmin(activeUser)
    || isUnderwriter(activeUser)
    || isSalesPerson(activeUser)
    || isInRole(Constants.roles.salesManagerId, loggedInUser);

  // User has clicked the "Create Application" button.
  const handleCreateApplicationClick = () => {
    // Alert user that they cannot create apps annd need to contact an admin to resolve the issue.
    if (!canEmployeeCreateNewApplication) {
      openDialog({
        fullScreen: false,
        children: (
          <ErrorDialog
            title='Create Application Not Allowed'
            errors={['You do not have permission to create a new application. Please contact an admin if you believe this is an error.']}
            closeDialog={closeDialog}
          />
        ),
      });
      return;
    }
    // If client can create finance applications, always show selector (Finance + Cash Deal options).
    // If only indirect is available, create indirect directly.
    // If only lease is available (no finance), create lease directly.
    // Otherwise, show window to select application type.
    if (!lookups.canCreateFinanceApplication && lookups.canCreateIndirectApplication && !lookups.canCreateLeaseApplication) {
      // Create an indirect application (i.e. Dealer App Form)
      openApplicationDialogByType(ClientApplicationType.Indirect);
    } else if (!lookups.canCreateFinanceApplication && !lookups.canCreateIndirectApplication && lookups.canCreateLeaseApplication) {
      // Create a lease application.
      openApplicationDialogByType(ClientApplicationType.Lease);
    } else {
      // Show selector so user can choose between Finance, Lease (if available), Indirect (if available), and Cash Deal
      setShowApplicationTypeSelector(true);
    }
  }

  // User has selected an application to create from the "Select App Type" window
  const openApplicationDialogByType = (applicationType: ClientApplicationType) => {
    // If this client is a finance company, open the dealer application form.
    // if (configuration.isFinanceCompany === true) {
    const heightValue = CSS.supports("height: 100dvb") ? "100dvb" : "100vh";
    if (applicationType === ClientApplicationType.Indirect) {
      openDialog({
        fullScreen: true,
        style:{maxHeight: heightValue},
        children: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: heightValue,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              padding: 16,
            }}
          >
            <DealerApplicationFormDialog
              clientId={configuration.clientId}
              defaultLocationId={isAdmin(activeUser) || isUnderwriter(activeUser) ? undefined : activeUser.organizationId}
              closeDialog={closeDialog}
              accept={(dealerApplication: DealerApplicationSubmission) => {
                dispatch(createAppFromDealerForm(dealerApplication));
                closeDialog();
              }}
            />
          </div>
        ),
      });
    } else {
      // Otherwise, use the default application form.
      // Client must have a form configuration saved before creating a new application.
      if (!isNil(applicantCreditAppFormConfig)) {
        let defaultApp: ApplicationWithMappedCustomers = {
          applicationDate: new Date(),
          customers: [createDefaultCustomer(0, true, applicantCreditAppFormConfig)],
          applicationCustomFields: customFields.filter(cf => cf.displayOnCreditApplicationForm === true).map((cf: ICustomField) => {
            return {
              customFieldId: cf.id,
              value: undefined,
            }
          })
        };
        // Create a list of OtherIncomeAndDebts for the customer, depending on the defaultMultiplicity value.
        for (let index = 0; index < (hasValue(applicantCreditAppFormConfig.otherIncomeAndDebt.defaultMultiplicity) ? applicantCreditAppFormConfig.otherIncomeAndDebt.defaultMultiplicity! : 1); index++) {
          defaultApp = {
            ...defaultApp,
            customers: defaultApp.customers.map(c => {
              if (c.mappingId === 0) {
                return {
                  ...c,
                  otherIncomeAndDebt: [...c.otherIncomeAndDebt, { mappingId: index }]
                }
              } else {
                return c;
              }
            })
          };
        }
        openDialog({
          fullScreen: true,
          children: (
            <ApplicationFormDialog
              requireSignatureOnForm={configuration.featureFlags?.[FeatureFlagIds.CustomerWillAcknowledgeCreditApplicationInformation] === true}
              applicantCreditAppFormConfiguration={applicantCreditAppFormConfig}
              coApplicantCreditAppFormConfiguration={coApplicantCreditAppFormConfig ?? applicantCreditAppFormConfig}
              maxNumberOfApplicants={configuration.maxNumberOfApplicants || 3} // Default to 3 applicants if no value is defined in client config
              shouldValidateSsn={configuration.featureFlags?.[FeatureFlagIds.ShouldValidateSSN] || false}
              shouldValidateAddress={configuration.featureFlags?.[FeatureFlagIds.ShouldValidateAddress] || false}
              accept={(app: Application) => {
                // Create the application with the selected application type.
                dispatch(createApp(
                  {
                    ...app,
                    quoteType: configuration?.clientId === 'ATC'
                      ? ApplicationQuoteType.Indirect
                      : applicationType === ClientApplicationType.Lease 
                        ? ApplicationQuoteType.Lease 
                        : ApplicationQuoteType.Finance,
                  },
                ));
                closeDialog();
              }}
              closeDialog={closeDialog}
              defaultApplication={defaultApp}
            />
          ),
        });
      } else {
        // Alert user that they need to contact an admin to set and save a form configuration.
        openDialog({
          fullScreen: false,
          children: (
            <ErrorDialog
              title='No Form Configuration'
              errors={['Please contact an admin to save a form configuration before creating a new application.']}
              closeDialog={closeDialog}
            />
          ),
        });
      }
    }
  };

  // Opens the cash deal window
  const handleOpenCashDealWindow = () => {
    setCashDealCustomer({});

    // Auto-select if only 1 store location available
    const storeLocations = organizations.filter(o => o.typeId.toLowerCase() === OrganizationIds.store);
    if (storeLocations.length === 1) {
      setCashDealLocationId(storeLocations[0].id);
    } else if (isAdmin(activeUser) || isUnderwriter(activeUser)) {
      setCashDealLocationId(null);
    } else {
      setCashDealLocationId(activeUser.organizationId);
    }

    setShowCashDealWindow(true);
  };

  // Submits the cash deal application
  const handleCashDealSubmit = () => {
    if (!cashDealLocationId) {
      dispatch(showErrorMessage('Please select a location'));
      return;
    }

    dispatch(createCashDealApplication({
      customer: {
        firstName: cashDealCustomer.firstName || '',
        lastName: cashDealCustomer.lastName || '',
        address: cashDealCustomer.address || '',
        city: cashDealCustomer.city || '',
        stateAbbreviation: cashDealCustomer.stateAbbreviation || '',
        zipCode: cashDealCustomer.zipCode || '',
        email: cashDealCustomer.email || '',
        phone: cashDealCustomer.phone || ''
      },
      organizationId: cashDealLocationId,
      ownerEmployeeId: activeUser.id
    }));

    setShowCashDealWindow(false);
  };

  // User has click on a row in the grid.
  const isRowNavigationRestricted = (item: IApplicationListRow): boolean =>
    isQueueNavigationRestrictedByRole &&
    !allowedQueueNavigationStatuses.some(s => s === item.statusId?.toUpperCase());

  const getRowNavigationRestrictionReason = (item: IApplicationListRow): string | undefined => {
    if (!isRowNavigationRestricted(item)) return undefined;

    const allowedStatusNames = appStatuses
      .filter(as => allowedQueueNavigationStatuses.includes(as.id.toUpperCase()))
      .map(as => as.name)
      .sort((a, b) => a.localeCompare(b));

    const currentStatus = item.status ?? appStatuses.find(as => as.id === item.statusId)?.name ?? 'this status';
    if (allowedStatusNames.length === 0) {
      return `You cannot open this application while it is in ${currentStatus}.`;
    }

    return `You cannot open this application while it is in ${currentStatus}. Allowed statuses: ${allowedStatusNames.join(', ')}.`;
  };

  const handleGridRowItemClick = (item: IApplicationListRow) => {
    // If the user's role(s) define AllowedQueueNavigationStatuses, only allow navigation
    // into applications whose status is in that list. Empty list = all statuses allowed.
    const restrictionReason = getRowNavigationRestrictionReason(item);
    if (restrictionReason) {
      openDialog({
        children: (
          <Window title="Cannot Open Application" onClose={closeDialog} width={450} height={220}>
            <p className={styles.queueDialogMessage}>{restrictionReason}</p>
            <WindowActionsBar>
              <KendoButton themeColor="primary" onClick={closeDialog}>OK</KendoButton>
            </WindowActionsBar>
          </Window>
        ),
      });
      return;
    }

    editOrder(item.id);
    if ((item.appSource?.toUpperCase() === 'DEALERTRACK' || item.appSource?.toUpperCase() === 'ROUTEONE') && item.numberOfUnviewedDealerServiceComments) {
      viewCommentsForApplication(item.id);
    }
  }

  // Determines if a status workflow action button should show for a given queue row.
  // Uses the shared hook with queueRowOnly filter so only items flagged with
  // showInApplicationQueueRow = true appear here.
  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const restrictionReason = getRowNavigationRestrictionReason(rowProps.dataItem);
    if (!restrictionReason) return trElement;

    const existingClassName = (trElement.props as any).className ?? '';
    const existingStyle: React.CSSProperties = (trElement.props as any).style ?? {};
    return React.cloneElement(trElement as React.ReactElement<any>, {
      className: `${existingClassName} ${styles.restrictedRow}`.trim(),
      style: { ...existingStyle, cursor: 'not-allowed' },
    });
  };

  const getQueueRowWorkflowAction = (item: IApplicationListRow): IApplicationStatusWorkflowItem | undefined => {
    if (!item.applicationQuoteType || !item.statusId) return undefined;
    const actions = getAvailableActions(item.statusId, Number(item.applicationQuoteType), { queueRowOnly: true });
    return actions[0];
  };

  const handleQueueRowActionClick = (e: React.MouseEvent, item: IApplicationListRow, workflowItem: IApplicationStatusWorkflowItem) => {
    e.stopPropagation();
    e.preventDefault();
    const nextStatusIsDealerReview = workflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.dealerReview.toUpperCase();
    const confirmMessage = nextStatusIsDealerReview
      ? 'This will convert the indirect quote to a finance quote and move the application to Dealer Review. This action cannot be undone.'
      : `This will move the application to "${workflowItem.nextStatus}". Continue?`;
    openDialog({
      children: (
        <Window title={`Confirm ${workflowItem.label}`} onClose={closeDialog} width={450} height={220}>
          <p style={{ margin: '10px 0' }}>{confirmMessage}</p>
          <WindowActionsBar>
            <KendoButton onClick={closeDialog}>Cancel</KendoButton>
            <KendoButton themeColor="primary" onClick={() => {
              closeDialog();
              (async () => {
                const movedApp = await dispatch(moveForwardAppFromQueue(item.id, workflowItem.nextStatusId));
                if (movedApp?.id) {
                  editOrder(movedApp.id);
                }
              })();
            }}>{workflowItem.label}</KendoButton>
          </WindowActionsBar>
        </Window>
      ),
    });
  };

  // --- BEGIN DEFINING OUR CUSTOM GRID CELLS FOR RENDERING APPLICATION DATA ---

  const ApplicationNumberCell = (props: GridRowProps) => {
    const appUrl = `/order/${props.dataItem.id}`;
    const restricted = isRowNavigationRestricted(props.dataItem);
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '8px', paddingTop: '12px', paddingBottom: '0px', paddingRight: '0px' }}>
        <Tooltip title={restricted ? 'You do not have access to open this application' : 'Open in new tab'}>
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '4px' }}>
            {restricted ? (
              <IconButton size='small' disabled component='span'>
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            ) : (
              <a
                href={appUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClickCapture={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                style={{ display: 'inline-flex', color: 'inherit', textDecoration: 'none' }}
              >
                <IconButton
                  size='small'
                  color='primary'
                  component='span'
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              </a>
            )}
          </span>
        </Tooltip>
        {addThousandsSeparator(props.dataItem.applicationNo)}
      </td>
    );
  };

  const ApplicationDateCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {new Date(props.dataItem.applicationDate).toLocaleString([], {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </td>
    );
  };

  const ApplicationExceptionDateCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {new Date(props.dataItem.latestExceptionDate).toLocaleString([], {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </td>
    );
  };


  const getFormattedDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString([], {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }



  const getApplicationSourceImagePath = (appSource: string | undefined): string | undefined => {
    switch (appSource?.toUpperCase()) {
      case 'DEALERTRACK':
        return '/assets/DT.png';
      case 'ROUTEONE':
        return '/assets/R1.png';
      default:
        return '/assets/AmsAnalytics.png';
    }
  }

  const getApplicationSourceImageComponent = (appSource: string | undefined, applicationQuoteType: ApplicationQuoteType): JSX.Element => {
    const imagePath = getApplicationSourceImagePath(appSource);
    const showAmsAnalyticsLogo = appSource?.toUpperCase() !== 'DEALERTRACK' && appSource?.toUpperCase() !== 'ROUTEONE';
    return hasValue(imagePath)
      ? showAmsAnalyticsLogo
        ? <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><img style={{ borderRadius: '50%', width: '30px', height: '30px' }}
              src={imagePath} alt={appSource} />{applicationQuoteTypeLabels[applicationQuoteType] ?? ''}</div>
        : <img style={{ borderRadius: '50%', width: '30px', height: '30px' }} src={imagePath} alt={appSource} />
      : <div></div>;
  }

  const ApplicationSourceCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '12px', paddingBottom: '0px', paddingRight: '0px' }} onClick={(e) => { e.stopPropagation(); handleGridRowItemClick(props.dataItem); }} role="button" tabIndex={0}>
        {getApplicationSourceImageComponent(props.dataItem.appSource, props.dataItem.applicationQuoteType)}
      </td>
    );
  };

  const ApplicationAssigneeCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {hasValue(props.dataItem.assigneeId) && getEmployeeName(employees, props.dataItem.assigneeId)}
      </td>
    );
  };

  const ApplicationLocationCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {configuration?.featureFlags?.[FeatureFlagIds.DisplayLocationDescription] !== true
          ? props.dataItem.location
          : props.dataItem.locationDescription}
      </td>
    );
  };

  const ApplicationVehicleLocationCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        <span
          style={{
            color: props.dataItem.location !== props.dataItem.vehicleLocation ? 'red' : '',
          }}
        >
          {configuration?.featureFlags?.[FeatureFlagIds.DisplayLocationDescription] !== true
            ? props.dataItem.vehicleLocation
            : props.dataItem.vehicleLocationDescription}
        </span>
      </td>
    );
  };

  const ApplicationChecklistCell = (props: GridRowProps) => {
    const checklistIds = props.dataItem.applicationRowChecklists.map((arc: IApplicationRowChecklist) => arc.checklistId);
    const canUserSeeAnyChecklist = canViewAnyAvailableChecklist(activeUser, checklistIds, lookups.employeeRoleChecklists);

    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {(props.dataItem.numberOfUnviewedTextMessages > 0 ||
          props.dataItem.numberOfUnviewedDealerServiceComments > 0 ||
          props.dataItem.numberOfApplicationExceptionsRaised > 0) && (
            <Tooltip
              title={
                <>
                  {props.dataItem.numberOfUnviewedTextMessages > 0 && (
                    <Typography style={{ fontSize: 12 }}>
                      Has {props.dataItem.numberOfUnviewedTextMessages} unviewed text messages.
                    </Typography>
                  )}
                  {props.dataItem.numberOfUnviewedDealerServiceComments > 0 && (
                    <Typography style={{ fontSize: 12 }}>
                      Has {props.dataItem.numberOfUnviewedDealerServiceComments} unviewed dealer service comments.
                    </Typography>
                  )}
                  {props.dataItem.numberOfApplicationExceptionsRaised > 0 && (
                    <Typography style={{ fontSize: 12 }}>
                      Has {props.dataItem.numberOfApplicationExceptionsRaised} application exceptions raised.
                    </Typography>
                  )}
                </>
              }
            >
              <NotificationImportantIcon fontSize='small' color='secondary' />
            </Tooltip>
          )}
        {props.dataItem.applicationRowChecklists?.length > 0 && canUserSeeAnyChecklist && (
          <Tooltip title={<ChecklistRowButtonToolTipText applicationRowChecklists={props.dataItem.applicationRowChecklists as IApplicationRowChecklist[]} lookups={lookups} />}>
            <CheckIcon
              fontSize='small'
              color={allApplicationRowChecklistsAreComplete(props.dataItem.applicationRowChecklists as IApplicationRowChecklist[])
                ? 'primary'
                : 'error'}
            />
          </Tooltip>
        )}
      </td>
    );
  };

  const ApplicationIncomeVerificationCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {
          props.dataItem.verifiedIncomeCount === props.dataItem.totalIncomeCount && (
            <Tooltip title={`${props.dataItem.verifiedIncomeCount} of ${props.dataItem.totalIncomeCount} incomes verified`}>
              <AttachMoneyIcon fontSize='small' style={{ color: '#648FFF' }} />
            </Tooltip>
          )
        }
        {
          props.dataItem.verifiedIncomeCount < props.dataItem.totalIncomeCount && (
            <Tooltip title={`${props.dataItem.verifiedIncomeCount} of ${props.dataItem.totalIncomeCount} incomes verified`}>
              <MoneyOffIcon fontSize='small' style={{ color: '#FE6100' }} />
            </Tooltip>
          )
        }
      </td>
    )
  };

  const AssignmentQueueOrderCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {props.dataItem.assignmentQueueOrder === -1 ? 'In Progress' : props.dataItem.assignmentQueueOrder}
      </td>
    );
  };

  const ApplicationStatusDateCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {new Date(props.dataItem.applicationStatusDate).toLocaleString([], {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </td>
    );
  };

  const AssignmentDateCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {new Date(props.dataItem.assignmentDate).toLocaleString([], {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </td>
    );
  };

  const ActionsCell = (props: GridRowProps & { showDownloadButton: boolean }) => {
    const downloadMode = getApplicationDownloadMode(configuration);
    const queueRowAction = getQueueRowWorkflowAction(props.dataItem);

    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all`, paddingLeft: '12px', paddingTop: '1​2px', paddingBottom: '0px', paddingRight: '0px' }}>
        {queueRowAction && (
          <Tooltip title={queueRowAction.label}>
            <IconButton
              onClickCapture={(e) => handleQueueRowActionClick(e, props.dataItem, queueRowAction)}
              color='success'
            >
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        )}
        {props.showDownloadButton === true && configuration?.featureFlags?.[FeatureFlagIds.HideDownloadAppButton] !== true && downloadMode !== 'hidden' && (
          <Tooltip title='Download application'>
            <IconButton
              onClickCapture={(e) => {
                downloadApp(props.dataItem.id, props.dataItem.applicationNo);
                e.stopPropagation();
                e.preventDefault();
              }}
              color='primary'
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
        {hasValue(props.dataItem.contactDetailUrl) === true && (
          <Tooltip title='View contact in CRM'>
            <IconButton
              onClickCapture={(e) => {
                // open url in new tab
                window.open(props.dataItem.contactDetailUrl, '_blank');
                e.stopPropagation();
                e.preventDefault();
              }}
              color='primary'
            >
              <AssignmentIndIcon />
            </IconButton>
          </Tooltip>
        )}
      </td>
    );
  };

  // --- END DEFINING OUR CUSTOM GRID CELLS ---

  // --- START Date Range Filter ---

  // Date range filter cell.
  const DateRangeFilterCell = (props: GridFilterCellProps) => {
    const [tempValue, setTempValue] = useState(props.value as { start: Date | null, end: Date | null });

    const dateInputSettings: DateRangePickerDateInputSettings = {
      format: "M/dd/yy",
      formatPlaceholder: "short",
      width: 130
    };

    const onChange = (event: DateRangePickerChangeEvent) => {
      setTempValue(event.value);

      if (event.value.start && event.value.end) {
        const value = {
          start: moment(event.value.start).startOf('day').toDate(),
          end: moment(event.value.end).endOf('day').toDate(),
        };
        props.onChange({
          value: value,
          operator: 'gte',
          syntheticEvent: event.syntheticEvent
        });
      }
    };

    const startDateString: string = !isNil(tempValue) && !isNil(tempValue.start)
      ? new Date(tempValue.start).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
      : '';

    const endDateString: string = !isNil(tempValue) && !isNil(tempValue.end)
      ? new Date(tempValue.end).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
      : (!isNil(tempValue) && !isNil(tempValue.start)) ? '--' : '';

    const displayDateString: string = !hasValue(startDateString) && !hasValue(endDateString)
      ? 'Select dates...'
      : startDateString + ' to ' + endDateString;

    return (
      <div className='k-filtercell' style={{ width: '100%' }} >
        <DateRangePicker
          onChange={onChange}
          value={tempValue}
          startDateInput={() => (
            <input
              type='text'
              readOnly
              value={!isNil(tempValue) ? new Date(tempValue.end!).toDateString() : ''}
              style={{ display: 'none' }}
            />
          )}
          endDateInput={() => (
            <input
              type='text'
              readOnly
              value={displayDateString}
              placeholder='Select dates...'
              className='k-input k-input-md k-rounded-md k-input-solid'
              style={{ cursor: 'pointer', width: '100%' }}
            />
          )}
          startDateInputSettings={dateInputSettings}
          endDateInputSettings={dateInputSettings}
          max={moment().add(1, 'days').toDate()}
          style={{ width: '100%' }}
        />
        <KendoButton
          title="Clear"
          disabled={!tempValue || (!tempValue?.start && !tempValue?.end)}
          onClick={(event: any) => {
            setTempValue({ start: null, end: null });
            props.onChange({
              value: { start: null, end: null },
              operator: 'gte',
              syntheticEvent: event.syntheticEvent
            });
          }}
          icon="filter-clear"
          style={{ alignSelf: 'flex-end', marginLeft: 5 }}
        />
      </div>
    );
  };

  // --- END Date Range Filter ---

  let primaryDateColumn = {};
  switch (props.applicationQueue.id) {
    case Constants.applicationQueues.exception:
      primaryDateColumn = { title: "Exception Date", cell: (props: any) => <ApplicationExceptionDateCell {...props} />, filter: 'date', filtercell: (props: any) => <DateRangeFilterCell {...props} />, width: '330px', field: 'latestExceptionDate' };
      break;
    case Constants.applicationQueues.rehash:
      primaryDateColumn = { title: "Rehash Date", filter: 'date', filtercell: (props: any) => <DateRangeFilterCell {...props} />, width: '330px', field: 'earliestRehashDate' };
      break;
    default:
      //primaryDateColumn = { title: "Date", cell: (props: any) => <ApplicationDateCell {...props} />, filter: 'date', filtercell: (props: any) => <DateRangeFilterCell {...props} />, width: '330px', field: 'appDate' };
      primaryDateColumn = { title: 'Date', filter: 'date', filtercell: (props: any) => <DateRangeFilterCell {...props} />, width: '330px', field: 'appDate' };
  }

  // List of columns for the generic grid
  let genericGridColumns: ApplicationQueueGridColumn[] = [];

  genericGridColumns.push(
    { title: "App", cell: (props: any) => <ApplicationNumberCell {...props} />, field: "applicationNo", width: "210px" },
    primaryDateColumn
  );

  if (configuration?.featureFlags?.[FeatureFlagIds.ShowModifiedDateColumn]) {
    genericGridColumns.push({ title: 'Last Activity Date', filter: 'date', filtercell: (props: any) => <DateRangeFilterCell {...props} />, width: '330px', field: 'lastModifiedDate' });
  }
  if (!configuration?.featureFlags?.[FeatureFlagIds.HideQueueSourceColumn]) {
    genericGridColumns.push({ title: "Source", cell: (props: any) => <ApplicationSourceCell {...props} />, field: "appSource", width: '200px' });
  }
  if (configuration?.featureFlags?.[FeatureFlagIds.ShowQueueAssigneeColumn]) {
    genericGridColumns.push({ title: "Assignee", cell: (props: any) => <ApplicationAssigneeCell {...props} />, field: "assignee", width: '200px' });
  }
  if (!configuration?.featureFlags?.[FeatureFlagIds.HideQueueSalespersonColumn]) {
    genericGridColumns.push({ title: `${employeeRoles.find(er => er.id === Constants.roles.salespersonId)?.name ?? "Salesperson"}`, field: "salespersonName", width: '200px' });
  }
  genericGridColumns.push({ title: `${employeeRoles.find(er => er.id === Constants.roles.underwriterId)?.name ?? "Underwriter"}`, field: "underwriter", width: '200px' });


  if (configuration?.featureFlags?.[FeatureFlagIds.UseApplicantQueueColumn]) {
    genericGridColumns.push({ title: "Applicant", field: "buyerName", width: "200px" });
    genericGridColumns.push({ title: "Co-Applicant", width: "200px", field: 'cobuyerName' });
  } else {
    genericGridColumns.push({ title: "Buyer's Name", field: "buyerName", width: "200px" });
    genericGridColumns.push({ title: "CB1", width: "200px", field: 'cobuyerName' });
  }

  genericGridColumns.push({
    title: "Status",
    field: "status",
    width: '180px',
    cell: (props: any) => (
      <StatusCell
        {...props}
        onClick={() => handleGridRowItemClick(props.dataItem)}
        queueRowAction={getQueueRowWorkflowAction(props.dataItem)}
        onQueueRowActionClick={handleQueueRowActionClick}
      />
    )
  });
  genericGridColumns.push({ title: "Location", field: "location", cell: (props: any) => <ApplicationLocationCell {...props} />, width: '250px' });

  // Note that the "ExcludeStockNumbers" flag hides the stock number from other locations in the interface.
  if (!configuration?.featureFlags?.[FeatureFlagIds.HideQueueStockNumberColumn] && configuration?.featureFlags?.[FeatureFlagIds.ExcludeStockNumbers] !== true) {
    genericGridColumns.push({ title: "Stock #", field: "stockNumber", width: '200px' });
  }
  if (!configuration?.featureFlags?.[FeatureFlagIds.HideQueueVehicleLocationColumn]) {
    genericGridColumns.push({ title: "Vehicle Location", field: "vehicleLocation", cell: (props: any) => <ApplicationVehicleLocationCell {...props} />, width: '250px' });
  }
  if (!configuration?.featureFlags?.[FeatureFlagIds.HideQueueScoreColumn]) {
    genericGridColumns.push({ title: "Score", field: "score", filter: 'numeric', width: "180px" });
  }


  // if (SOME_FF_TO_SHOW_THESE_COLUMNS) {
  //   genericGridColumns.push({ title: "Verified Income?", cell: (props: any) => <ApplicationIncomeVerificationCell {...props} />, width: '200px', field: 'areAllIncomesVerified', filter: 'boolean', preventSorting: true });
  //   genericGridColumns.push({ title: "Checklist", cell: (props: any) => <ApplicationChecklistCell {...props} />, width: '120px;', preventFiltering: true, preventSorting: true });
  // }
  // Dynamically append grid columns, depending on component props.
  if (props.applicationQueue.id === Constants.applicationQueues.myWork && configuration?.featureFlags?.[FeatureFlagIds.SortMyWorkQueueForUnderwritersByApplicationStatusDate]) {
    genericGridColumns.push({ title: "UW Queue Order", cell: (props: any) => <AssignmentQueueOrderCell {...props} />, width: '100px', preventFiltering: true, preventSorting: true });
  }
  if (props.applicationQueue.displayAssignmentDate) {
    genericGridColumns.push({ title: "Status Changed Date", cell: (props: any) => <ApplicationStatusDateCell {...props} />, width: '100px', field: 'statusChangedDate', preventFiltering: true, preventSorting: true });
    genericGridColumns.push({ title: "Assigned At", cell: (props: any) => <AssignmentDateCell {...props} />, width: '100px', preventFiltering: true, preventSorting: true });
  }

  genericGridColumns.push({ title: "State", field: "applicantState", filter: 'text', width: "180px" });

  // Loop through the ApplicationQueueCustomFields and create the columns for those
  props.applicationQueue.applicationQueueCustomFields?.map((a) => {
    genericGridColumns.push({ title: a.displayName, field: a.fieldName, preventFiltering: true, preventSorting: true, width: "180px" });
  });

  // IF they have the configuration for this, it will filter and sort the columns by the config item
  const columnSort = configuration.applicationQueueColumnSortOrder;
  if (columnSort && columnSort.length > 0) {
    genericGridColumns = genericGridColumns.filter(col => columnSort?.includes(col.field!));
    genericGridColumns.sort(function (a, b) {
      return columnSort.indexOf(a!.field!) - columnSort!.indexOf(b!.field!);
    });
  }

  genericGridColumns.push({ title: "", cell: (props: any) => <ActionsCell {...props} showDownloadButton={!props.onSelect} />, width: '100px;', preventFiltering: true, preventSorting: true });

  // View recieved comments for a specific application.
  async function viewCommentsForApplication(applicationId: string) {
    await viewCommentsReceivedForApplication(applicationId, dispatch);
  }
  const queueItems = resolvedData?.applications?.map((a: IApplicationListRow) => {
    const earliestRehashDate = a.earliestRehashDate ? getFormattedDate(a.earliestRehashDate.toString()) : '';
    const underwriter = employees.find((e) => e.id === getAssignedEmployeeByRoleAndSequence(a.applicationAssignments, Constants.roles.underwriterId, 1)?.assignedToId);
    const underWriterName = underwriter ? `${underwriter.firstName} ${underwriter.lastName}` : '';
    const salesperson = employees.find((e) => e.id === getAssignedEmployeeByRoleAndSequence(a.applicationAssignments, Constants.roles.salespersonId, 1)?.assignedToId);
    const salespersonName = salesperson ? `${salesperson.firstName} ${salesperson.lastName}` : '';
    const cobuyerName = a.coBuyerLastName && `${a.coBuyerFirstName} ${a.coBuyerLastName}`;
    const buyerName = a.customerId && `${a.customerFirstName} ${a.customerLastName}`;
    const updatedApplicationRow: any = produce(a, (a) => {
      a.applicationQueueCustomFieldValues.forEach(afv => {
        a[afv.fieldName] = afv.value
      });
      a.earliestRehashDate = earliestRehashDate;
      (a as any).appDate = (getFormattedDate(a.applicationDate.toString()) as any);
      (a as any).lastModifiedDate = (getFormattedDate((a as any).modifiedDate.toString()) as any);
      (a as any).underwriter = underWriterName;
      (a as any).buyerName = buyerName;
      (a as any).cobuyerName = cobuyerName;
      (a as any).status = appStatuses.find(as => as.id === a.statusId)?.name ?? "";
      (a as any).salespersonName = salespersonName;
    })
    return updatedApplicationRow;
  }) ?? [];
  const exportExcel = () => {
    exporterRef.current?.save();
  };

  return (
    <div className={styles.applicationQueueContainer}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', height: '50px', color: 'var(--kendo-color-primary)', margin: 0 }}>
        {
          props.applicationQueueLoadingDetail.loading && (<LoadingPanel label='Fetching new data...' style={{ fontSize: '20px', fontWeight: 500 }} />)
        }
        {
          !props.applicationQueueLoadingDetail.loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              height: '50px',
              color: 'var(--kendo-color-primary)',
              margin: 0
            }}>
              {`Orders: ${((resolvedData?.total || 0) < props.applicationQueueLoadingDetail.filters.skip! + 1) ? (resolvedData?.total || 0) : (props.applicationQueueLoadingDetail.filters.skip! + 1)} - ${((resolvedData?.total || 0) < props.applicationQueueLoadingDetail.filters.skip! + props.applicationQueueLoadingDetail.filters.take!) ? (resolvedData?.total || 0) : (props.applicationQueueLoadingDetail.filters.skip! + props.applicationQueueLoadingDetail.filters.take!)} of ${addThousandsSeparator(resolvedData?.total)}`}
            </div>
          )
        }
        {configuration?.featureFlags?.[FeatureFlagIds.CanCreateNewApp] === true && (
          <Button
            style={{
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              minWidth: `250px`,
            }}
            variant='contained'
            color='primary'
            onClick={() => handleCreateApplicationClick()}
          >
            <AddIcon style={{ color: `primary`, marginRight: `5px` }} />
            Create Application
          </Button>
        )}
      </div>
      {pagedOutput ? (
        <LocalizationProvider language={currentLocale.language} >
          <IntlProvider locale={currentLocale.locale}>
            <ExcelExport data={queueItems} ref={exporterRef}>
              <Grid
                data={queueItems}
                filterable={true}
                sortable={true}
                total={resolvedData.total}
                skip={props.applicationQueueLoadingDetail.filters.skip}
                take={props.applicationQueueLoadingDetail.filters.take}
                pageable={{ pageSizes: [10, 20, 40, 100, 500, 1000, 5000], buttonCount: 5 }}
                onDataStateChange={(event: GridDataStateChangeEvent) => {
                  dispatch(updateApplicationQueueFilterSuccess(props.applicationQueue.id, event.dataState));
                }}
                resizable={true}
                rowRender={rowRender}
                onRowClick={(event: GridRowClickEvent) => {
                  handleGridRowItemClick(event.dataItem);
                }}
                sort={props.applicationQueueLoadingDetail.filters.sort}
                filter={props.applicationQueueLoadingDetail.filters.filter}
                style={hasCurrentReleaseNotes ? { height: 'calc(100vh - 293px)' } : { height: 'calc(100vh - 192px)' }}
                groupable={false}
              >
                <GridToolbar>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                    {
                      userHasPermission(loggedInUser, permissions.applicationExportToExcel, PermissionSet.R) && (
                        <Button title='Export to Excel' variant='contained' color='secondary' onClick={exportExcel}>
                          Export to Excel
                        </Button>
                      )}
                    {
                      userHasPermission(loggedInUser, permissions.applicationListReport, PermissionSet.R) && (
                        <Button title='Extended Application Export' variant='contained' color='secondary'
                          onClick={async () => {
                            const bearerToken = await authService.getToken();
                            const config: any = await fetchConfig();
                            const baseUrl = `${config.api_endpoint}`;
                            openDialog({
                              children: (
                                <ReportViewerDialog
                                  title={"Application List Report"}
                                  reportName={props.configuration.applicationListReport!}
                                  parameters={[]}
                                  baseUrl={baseUrl}
                                  clientId={configuration?.clientId}
                                  closeDialog={closeDialog}
                                  bearerToken={bearerToken!}
                                />
                              ),
                              fullScreen: true,
                            });
                          }}
                        >
                          Application List Report
                        </Button>
                      )
                    }
                  </div>
                </GridToolbar>
                {genericGridColumns.map((column, index) => {
                  return (
                    <Column
                      className={'clickableRow'}
                      key={index}
                      field={column.field}
                      title={column.title}
                      editable={false}
                      cell={column.cell}
                      resizable={true}
                      sortable={!column.preventSorting}
                      filter={column.filter}
                      filterable={!column.preventFiltering}
                      groupable={false}
                      width={column.width}
                      filterCell={column.filtercell}
                      filterTitle={column.filterTitle}
                    />
                  );
                })}
              </Grid>
            </ExcelExport>
          </IntlProvider>
        </LocalizationProvider>
      ) : null}
      {showApplicationTypeSelector && (
        <Window width={410} height={240} title="Choose Application Type" onClose={() => setShowApplicationTypeSelector(false)} themeColor='primary'>
          <p style={{ fontSize: '16px' }}>What type of application would you like to create?</p>
          <div style={{ display: 'flex', gap: '10px' }}>
            {lookups.canCreateFinanceApplication && (
              <KendoButton
                onClick={() => {
                  openApplicationDialogByType(ClientApplicationType.Finance);
                  setShowApplicationTypeSelector(false);
                }}
                themeColor="base"
              >
                Direct Financing
              </KendoButton>
            )}
            {lookups.canCreateLeaseApplication && (
              <KendoButton
                onClick={() => {
                  openApplicationDialogByType(ClientApplicationType.Lease);
                  setShowApplicationTypeSelector(false);
                }}
                themeColor="base"
              >
                Direct Leasing
              </KendoButton>
            )}
            {lookups.canCreateIndirectApplication && (
              <KendoButton
                onClick={() => {
                  openApplicationDialogByType(ClientApplicationType.Indirect);
                  setShowApplicationTypeSelector(false);
                }}
                themeColor='base'
              >
                Indirect
              </KendoButton>
            )}
            {lookups.canCreateFinanceApplication && (
              <KendoButton
                onClick={() => {
                  handleOpenCashDealWindow();
                  setShowApplicationTypeSelector(false);
                }}
                themeColor='base'
              >
                Cash Deal
              </KendoButton>
            )}
          </div>
          <WindowActionsBar>
            <KendoButton onClick={() => setShowApplicationTypeSelector(false)} themeColor="error">Cancel</KendoButton>
          </WindowActionsBar>
        </Window>
      )}
      {showCashDealWindow && (
        <Window title="Cash Deal Application" onClose={() => setShowCashDealWindow(false)} themeColor='primary' stage='FULLSCREEN'>
          <CashDealApplication
            customer={cashDealCustomer}
            states={states}
            locations={organizations.filter(o => o.typeId.toLowerCase() === OrganizationIds.store).map((o, index) => ({ id: o.id, name: o.name ?? '', description: o.description ?? '', sortOrder: index, regionId: o.parentOrganizationId ?? '', amsCode: o.amsCode ?? '' }))}
            locationId={cashDealLocationId}
            onLocationChange={(id) => setCashDealLocationId(id)}
            onChange={(customer) => setCashDealCustomer(customer)}
          />
          <WindowActionsBar>
            <KendoButton onClick={() => setShowCashDealWindow(false)} themeColor="base">Cancel</KendoButton>
            <KendoButton onClick={handleCashDealSubmit} themeColor="primary">Submit</KendoButton>
          </WindowActionsBar>
        </Window>
      )}
    </div>
  );
}
